import { Image as ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { getLoreStatusVariant, type LoreEntryView } from "@/features/lore";
import { cn } from "@/lib/utils";

type LoreEntryListProps = {
  activeEntryId: string | null;
  entries: LoreEntryView[];
  onOpenEntry: (entryId: string) => void;
};

export function LoreEntryList({
  activeEntryId,
  entries,
  onOpenEntry,
}: LoreEntryListProps) {
  const { i18n, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "short",
  });

  if (entries.length === 0) {
    return (
      <div className="rounded-[24px] border border-dashed border-border/70 bg-white/40 px-4 py-8 text-center text-sm text-muted-foreground">
        {t("lore.entries.noResults")}
      </div>
    );
  }

  return (
    <ul aria-label={t("lore.entries.listLabel")} className="space-y-3">
      {entries.map((entry) => (
        <li key={entry.id}>
          <button
            className={cn(
              "w-full rounded-[1.2rem] px-3 py-3 text-left transition-colors",
              entry.id === activeEntryId
                ? "bg-[#efe2cf] text-foreground"
                : "text-muted-foreground hover:bg-white/65 hover:text-foreground",
            )}
            onClick={() => onOpenEntry(entry.id)}
            type="button"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <p className={cn("text-sm font-medium", entry.id === activeEntryId ? "text-foreground" : "")}>{entry.title}</p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{t(entry.scopeBadgeKey)}</Badge>
                  <Badge variant={getLoreStatusVariant(entry.canonStatus)} className="text-[10px] px-1.5 py-0">
                    {t(entry.canonStatusLabelKey)}
                  </Badge>
                </div>
              </div>
              {entry.hasCoverImage ? (
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <ImageIcon className="h-3 w-3" />
                  {t("lore.fields.cover")}
                </div>
              ) : null}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>{t(entry.typeLabelKey)}</span>
              <span>{t("lore.entries.updatedAt", { date: dateFormatter.format(new Date(entry.updatedAt)) })}</span>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
